import { useState } from "react";
import api from "@/lib/api";

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

const fieldBase = (focused) => ({
  display: "block",
  width: "100%",
  padding: "13px 16px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: "Inter, sans-serif",
  color: "#1C3545",
  background: "#F5FAFC",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 200ms ease, box-shadow 200ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
});

export default function ForgotPasswordPage({ onBack, onLoginClick }) {
  const [email, setEmail]     = useState("");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState("");

  const canSubmit = !loading && email.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "#EDF4F7",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />

      <div
        style={{
          position: "relative",
          zIndex: 2,
          background: "#FFFFFF",
          border: "1px solid rgba(90,144,168,0.18)",
          borderRadius: 22,
          padding: "40px 36px 36px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 4px 32px rgba(90,144,168,0.12)",
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <span className="font-serif-display" style={{ fontSize: 19, color: "#1C3545", letterSpacing: "-0.035em" }}>
            Supercore Longevity
          </span>
          <div aria-hidden style={{ width: 28, height: 2, background: "#8AAEBB", borderRadius: 99, margin: "10px auto 0", opacity: 0.8 }} />
        </div>

        {sent ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 26 }}>
              ✓
            </div>
            <h1 style={{ margin: "0 0 10px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              Check your email
            </h1>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>
              If <strong style={{ color: "#1C3545" }}>{email}</strong> is registered with an email &amp; password account, you'll receive a reset link shortly.
            </p>
            <div style={{ marginBottom: 24, padding: "11px 14px", borderRadius: 9, background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.15)", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
              No email? If you signed up with <strong style={{ color: "#4D7A8E" }}>Continue with Google</strong>, password reset doesn't apply — use Google Sign-In instead.
            </div>
            <p style={{ margin: "0 0 4px", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
              Still nothing? Check your spam folder, or
            </p>
            <button
              type="button"
              onClick={() => { setSent(false); setEmail(""); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#5A90A8", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", padding: 0 }}
            >
              try a different email
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              Forgot password?
            </h1>
            <p style={{ margin: "0 0 28px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.55 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>

            {error && (
              <div style={{ marginBottom: 18, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: 22 }}>
                <label htmlFor="fp-email" style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#4D7A8E", marginBottom: 8, fontFamily: "Inter, sans-serif" }}>
                  Email address
                </label>
                <input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  style={fieldBase(focused)}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  display: "block", width: "100%", padding: "13px",
                  background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)",
                  color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)",
                  border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                  fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed",
                  letterSpacing: "0.01em", transition: "background 200ms ease",
                }}
                onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = "#4D7A8E"; }}
                onMouseLeave={(e) => { if (canSubmit) e.currentTarget.style.background = "#5A90A8"; }}
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}

        <button
          type="button"
          onClick={onLoginClick || onBack}
          style={{ display: "block", width: "100%", textAlign: "center", marginTop: 24, background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", fontSize: 13, fontFamily: "Inter, sans-serif", transition: "color 150ms ease" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#1C3545")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9BBCCC")}
        >
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}
